import React from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import notFoundNewConsumerApp from 'commons/notFoundNewConsumerApp';
import PDPDesktop from './PDPDesktop';
import PDPMobile from './PDPMobile';
import Component404 from '../404';
import MobileAndTabletOnly from '../MobileAndTabletOnly';
import DesktopOnly from '../DesktopOnly';
import PDPSEOTags from './PDPSEOTags';
import PDPFooter from './PDPFooter';
import PDPContext from './state/context/PDPContext';
import usePDPLuxAnalytics from './hooks/usePDPLuxAnalytics';
import usePDPCreateContextValue from './hooks/usePDPCreateContextValue';
import PDPPageAnalytics from './PDPPageAnalytics';

const PDP = ({
  slugOverride = null,
  isEditOrder = false,
  pdpIsModularBYOProduct = false,
}) => {
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? null;

  const pdpContextValue = usePDPCreateContextValue({
    isEditOrder,
    pdpIsModularBYOProduct,
    slug,
  });

  usePDPLuxAnalytics();

  if (!isNewConsumerApp && pdpContextValue.basicDetails.isProductNotFound) {
    return <Component404 />;
  }

  if (isNewConsumerApp && pdpContextValue.basicDetails.isProductNotFound) {
    return notFoundNewConsumerApp();
  }

  const {
    isOutOfStock = false,
    showNotification,
    isEFGInternal,
    isDiscontinued = false,
    isETALoading = false,
    isETAVariant = false,
    discontinuedCategoryMsg = null,
  } = pdpContextValue.stockAvailability?.data || {};

  return (
    <>
      <PDPContext.Provider value={pdpContextValue}>
        <PDPSEOTags isOutOfStock={isOutOfStock} />

        {!isNewConsumerApp && (
          // CON-6491: Temporarily, we have disabled the mobile version of PDP
          // in the new Consumer app while fixing other loading issues.
          <MobileAndTabletOnly>
            <PDPMobile
              slug={slug}
              isEditOrder={isEditOrder}
              isOutOfStock={isOutOfStock}
              showOutOfStockNotification={showNotification}
              pdpIsModularBYOProduct={pdpIsModularBYOProduct}
              showDiscontinuedNotification={isDiscontinued}
              isETALoading={isETALoading}
              isETAVariant={isETAVariant}
              discontinuedCategoryMsg={
                isDiscontinued ? discontinuedCategoryMsg : null
              }
            />
          </MobileAndTabletOnly>
        )}

        <DesktopOnly>
          <PDPDesktop
            slug={slug}
            isEditOrder={isEditOrder}
            isOutOfStock={isOutOfStock}
            showOutOfStockNotification={showNotification}
            pdpIsModularBYOProduct={pdpIsModularBYOProduct}
            isEFGInternal={isEFGInternal}
            showDiscontinuedNotification={isDiscontinued}
            isETALoading={isETALoading}
            isETAVariant={isETAVariant}
            discontinuedCategoryMsg={
              isDiscontinued ? discontinuedCategoryMsg : null
            }
          />
        </DesktopOnly>

        <PDPPageAnalytics />
      </PDPContext.Provider>

      <PDPFooter />
    </>
  );
};

PDP.propTypes = {
  slugOverride: PropTypes.string,
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
};

export default PDP;
